/**
 * YB-2159 — PAPI refuses a pass whose client role's requirements expire DURING it (YB-2157) with
 * `errorCode: requirements_expiring` and `expiring_requirements`. The portal builds its message from
 * those — never from `errorText`, which is PAPI's English sentence — so it reads in the user's
 * language and names each requirement with its date.
 */

export const REQUIREMENTS_EXPIRING = 'requirements_expiring'

/** The GraphQL selection every pass create / edit / import operation asks for. */
export const EXPIRING_REQUIREMENTS_FIELDS = `errorCode
        expiring_requirements { requirement_id name expiration_date row holder_name holder_mobile }`

export interface ExpiringRequirement {
  requirement_id: number
  name: string
  /** ISO instant. */
  expiration_date: string
  /** Excel row, 1 = the first row under the header; null on an edit. */
  row?: number | null
  holder_name?: string | null
  holder_mobile?: string | null
}

/** One refused holder (a row of a bulk create, or the one pass of a single create / edit). */
export interface ExpiringRequirementsHolder {
  row: number | null
  holder_name: string | null
  holder_mobile: string | null
  requirements: ExpiringRequirement[]
}

interface Translator {
  instant(key: string, params?: object): string
}

const KEY = 'REQUIREMENT_SUBMISSIONS.EXPIRING'

/** dd/MM/yyyy in Israel — the date the holder's requirement is shown with everywhere else. */
export function formatExpiryDate(iso: string): string {
  const date = new Date(iso)
  if (isNaN(date.getTime())) return iso
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('day')}/${get('month')}/${get('year')}`
}

/** Groups the flat list (one entry per row × requirement) by refused row, in row order. */
export function groupExpiringRequirements(list: ExpiringRequirement[] | null | undefined): ExpiringRequirementsHolder[] {
  const holders = new Map<string, ExpiringRequirementsHolder>()
  for (const item of list ?? []) {
    const key = `${item.row ?? ''}|${item.holder_mobile ?? ''}`
    if (!holders.has(key)) holders.set(key, { row: item.row ?? null, holder_name: item.holder_name ?? null, holder_mobile: item.holder_mobile ?? null, requirements: [] })
    holders.get(key)!.requirements.push(item)
  }
  return [...holders.values()].sort((a, b) => (a.row ?? 0) - (b.row ?? 0))
}

/** "Work at height: expires 17/11/2026", localised. */
export function describeExpiringRequirement(translate: Translator, item: ExpiringRequirement): string {
  return translate.instant(`${KEY}.ITEM`, { name: item.name, date: formatExpiryDate(item.expiration_date) })
}

/**
 * The full message for a refused single create / edit, or a bulk create: who (when known), each
 * requirement with its date, and what the holder must do. One block per refused holder.
 */
export function buildRequirementsExpiringMessage(translate: Translator, list: ExpiringRequirement[] | null | undefined): { summary: string; detail: string } {
  const holders = groupExpiringRequirements(list)
  const several = holders.length > 1
  const blocks = holders.map((holder) => {
    const name = holder.holder_name?.trim()
    const heading = several
      ? translate.instant(`${KEY}.ROW`, { row: holder.row ?? '', holder: name || holder.holder_mobile || '' })
      : name
        ? translate.instant(`${KEY}.INTRO_HOLDER`, { holder: name })
        : translate.instant(`${KEY}.INTRO`)
    return [heading, ...holder.requirements.map((r) => `• ${describeExpiringRequirement(translate, r)}`)].join('\n')
  })
  return {
    summary: translate.instant(several ? `${KEY}.ROWS_TITLE` : `${KEY}.TITLE`),
    detail: [...blocks, translate.instant(`${KEY}.INSTRUCTION`)].join('\n\n'),
  }
}
